<script lang="ts" setup>
import { AanvragenDomainEntitiesMutatiesoort as Mutatiesoort } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMutatiesoort';
import { type SUREbusinessCommonDomainSoortVerzekeringEnum as SoortVerzekering } from '@services/aanvragen-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import { useMutatieRedenenGet } from '@services/aanvragen-public/orval-api/mutatie-redenen-api/mutatie-redenen-api';
import { useProductMaatschappijGetMelding } from '@services/producten-public/orval-api/product-maatschappij-api/product-maatschappij-api';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import { parseDateOnlyAsLocalTime } from '@tools/dates';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useId } from '@tools/composables/id';
import { z } from 'zod';
import { usePakketpolisStore } from '../pakketpolisStore';

const props = defineProps<{
  pakketpolisnummer: string;
  polisnummer: string;
  soortVerzekering: SoortVerzekering;
  productmaatschappijId?: string;
}>();

const { pakketpolisnummer, polisnummer, soortVerzekering, productmaatschappijId } = toRefs(props);

const pakketpolisStore = usePakketpolisStore();

const pakketpolis = computed(() => pakketpolisStore.getPakketpolis(pakketpolisnummer.value));

const mutatiedatumBeperking = computed(
  () =>
    pakketpolis.value?.mutatieActies.subpolisMutatieActies.find(
      (s) => s.polisnummer === polisnummer.value,
    )?.muteren.mutatiedatumAlleenMogelijkVanaf,
);

const minimumMutatiedatum = computed(() =>
  parseDateOnlyAsLocalTime(mutatiedatumBeperking.value?.datum),
);

const { data: melding } = useProductMaatschappijGetMelding(productmaatschappijId, {
  query: {
    select: (res) => res.result,
  },
});

const meldingTonen = computed(() => melding.value?.melding && melding.value?.melding.length > 0);

const { data, isPending } = useMutatieRedenenGet(
  computed(() => ({
    mutatiesoort: Mutatiesoort.Mutatie,
    soortVerzekering: soortVerzekering.value,
  })),
  {
    query: {
      select: (res) => res.result,
    },
  },
);

const validationSchema = computed(() =>
  toTypedSchema(
    z
      .object({
        gekozenReden: z.string().min(1, 'Vul een mutatiereden in.'),
        redenAnders: z
          .string()
          .max(40, 'De royementsreden mag maximaal 40 karakters bevatten')
          .optional(),
        gekozenDatum: z.date().refine(
          (date) => minimumMutatiedatum.value === undefined || date >= minimumMutatiedatum.value,
          () => ({ message: mutatiedatumBeperking.value?.melding }),
        ),
      })
      .superRefine((input, ctx) => {
        const isRedenGeldig =
          input.gekozenReden !== 'anders' ||
          (!!input.redenAnders && !!input.redenAnders.trim().length);
        if (!isRedenGeldig) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul een mutatiereden in.',
            path: ['redenAnders'],
          });
        }
      }),
  ),
);

const { errors, useFieldModel, handleSubmit, validateField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    gekozenDatum: new Date(),
  },
});

watch(polisnummer, () => resetForm());
const gekozenReden = useFieldModel('gekozenReden');
const gekozenDatum = useFieldModel('gekozenDatum');
const redenAnders = useFieldModel('redenAnders');

watch(gekozenDatum, () => validateField('gekozenDatum'));

const aanvullendeRedenField = ref<InstanceType<typeof TextField>>();

watch(gekozenReden, (e) =>
  nextTick(() => {
    if (e === 'anders') aanvullendeRedenField.value?.focus();
  }),
);

const options = computed(() =>
  data.value?.map((x) => ({
    label: x.omschrijving + (x.interneOptie ? ' (Interne optie)' : ''),
    value: x.omschrijving,
  })),
);

const isOpen = defineModel<boolean>('isOpen');
const datumMutatieId = useId('datum-mutatie');

const opslaan = handleSubmit((values) => {
  pakketpolisStore.setSubpolisGemuteerd(
    pakketpolisnummer.value,
    polisnummer.value,
    values.gekozenDatum,
    values.gekozenReden === 'anders' ? values.redenAnders! : values.gekozenReden,
  );

  isOpen.value = false;
});
</script>
<template>
  <VModal
    overflow
    confirm-button-text="Mutatie opslaan"
    @close-modal="isOpen = false"
    @confirm-action="opslaan()"
    invert-header
    :title="`Mutatie ${polisnummer}`"
    :is-active="isOpen"
    data-testid="sub-polis-muteer-modal"
  >
    <VInfoBox type="info" v-if="meldingTonen">
      <div class="whitespace-pre-wrap">{{ melding?.melding }}</div>
    </VInfoBox>
    <FieldRowGroup :gap="2">
      <FieldRowV2 label="Reden mutatie" :errors="[errors.gekozenReden]">
        <VSkeleton height="lg" v-if="isPending" />
        <SelectField
          v-model:value="gekozenReden"
          v-else
          :options="[...options, { label: 'Andere reden...', value: 'anders' }]"
        />
      </FieldRowV2>
      <FieldRowV2 :errors="gekozenReden === 'anders' ? [errors.redenAnders] : []">
        <ExpandTransition>
          <TextField
            v-if="gekozenReden === 'anders'"
            :rows="2"
            :max-length="40"
            v-model:value="redenAnders"
            ref="aanvullendeRedenField"
            placeholder="Geef een mutatiereden op"
          />
        </ExpandTransition>
      </FieldRowV2>
      <FieldRowV2 label="Datum mutatie" :label-for="datumMutatieId" :errors="[errors.gekozenDatum]">
        <template #subLabel>
          Deze datum kan niet voor de ingangsdatum van de lopende polis liggen.
        </template>

        <VDatePicker
          :id="datumMutatieId"
          v-model="gekozenDatum"
          :invalid="!!errors?.gekozenDatum"
          :clearable="false"
          :min-date="minimumMutatiedatum"
          data-testid="mutatiedatum"
        />
      </FieldRowV2>
    </FieldRowGroup>
  </VModal>
</template>
